import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import TextareaAutosize from 'react-textarea-autosize';
import { Dialog, DialogDismiss, DialogHeading, useDialogState } from 'ariakit/dialog';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import ButtonPrimary from '../ButtonPrimary';
import styles from './LimeHelpdeskForm.module.scss';

const URL = '/wt/api/nextjs/v1/send_contact_form/?type=helpdesk';

const LimeHelpdeskForm = ({ formProps, csrf }) => {
    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [submitting, setSubmitting] = useState(false);
    const [ref, isVisible] = useIntersectionObserver();
    const dialog = useDialogState({ animated: true });
    const { t } = useTranslation();

    useEffect(() => {
        if (!dialog.open) {
            reset();
        }
    }, [dialog.open]);

    const handleSubmitForm = async (data) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            if (!executeRecaptcha) throw new Error('reCAPTCHA not ready');
            const token = await executeRecaptcha('helpdesk_form');
            if (!token) throw new Error('Failed to get reCAPTCHA token');

            const formData = new FormData();
            formData.append('recaptcha_token', token);
            formData.append('how_can_we_help', data.how_can_we_help);
            formData.append('email', data.email);
            formData.append('person_fullname', data.person_fullname);
            formData.append('companyname', data.companyname);
            formData.append('role_title', data.role_title);

            const res = await fetch(URL, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-CSRFToken': csrf,
                },
                body: formData,
                credentials: 'include',
            });

            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                console.error('Submit failed', parsed);
                throw new Error(parsed?.message || 'Submission failed');
            }

            dialog.show();
        } catch (error) {
            console.error('Error:', error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const formClasses = classNames(styles['Root'], 'black-glass', {
        [styles['Visible']]: isVisible,
    });

    return (
        <>
            <form onSubmit={handleSubmit(handleSubmitForm)} noValidate ref={ref} className={formClasses}>
                <p className={styles['Description']}>{t('LimeHelpdeskForm.requiredNote')}</p>

                <div className={styles['Fieldset']}>
                    <div className={styles['Field']}>
                        <div
                            className={classNames(styles['InputField'], {
                                [styles['HasValue']]: watch('person_fullname'),
                                [styles['HasError']]: errors.person_fullname,
                            })}>
                            <input
                                id="person_fullname"
                                type="text"
                                autoComplete="name"
                                maxLength={150}
                                className={styles['Input']}
                                aria-invalid={!!errors.person_fullname}
                                aria-describedby={errors.person_fullname ? 'person_fullname-error' : null}
                                {...register('person_fullname', { required: true })}
                            />
                            <label className={styles['Label']} htmlFor="person_fullname">
                                {t('LimeHelpdeskForm.labels.fullName')} *
                            </label>
                        </div>
                        {errors.person_fullname && (
                            <ErrorMessage id="person_fullname-error">
                                {t('LimeHelpdeskForm.errors.required')}
                            </ErrorMessage>
                        )}
                    </div>

                    <div className={styles['Field']}>
                        <div
                            className={classNames(styles['InputField'], {
                                [styles['HasValue']]: watch('companyname'),
                                [styles['HasError']]: errors.companyname,
                            })}>
                            <input
                                id="companyname"
                                type="text"
                                autoComplete="organization"
                                maxLength={150}
                                className={styles['Input']}
                                aria-invalid={!!errors.companyname}
                                aria-describedby={errors.companyname ? 'companyname-error' : null}
                                {...register('companyname', { required: true })}
                            />
                            <label className={styles['Label']} htmlFor="companyname">
                                {t('LimeHelpdeskForm.labels.companyName')} *
                            </label>
                        </div>
                        {errors.companyname && (
                            <ErrorMessage id="companyname-error">{t('LimeHelpdeskForm.errors.required')}</ErrorMessage>
                        )}
                    </div>

                    <div className={styles['Field']}>
                        <div
                            className={classNames(styles['InputField'], {
                                [styles['HasValue']]: watch('role_title'),
                                [styles['HasError']]: errors.role_title,
                            })}>
                            <input
                                id="role_title"
                                type="text"
                                autoComplete="organization-title"
                                maxLength={150}
                                className={styles['Input']}
                                aria-invalid={!!errors.role_title}
                                aria-describedby={errors.role_title ? 'role_title-error' : null}
                                {...register('role_title', { required: true })}
                            />
                            <label className={styles['Label']} htmlFor="role_title">
                                {t('LimeHelpdeskForm.labels.roleTitle')} *
                            </label>
                        </div>
                        {errors.role_title && (
                            <ErrorMessage id="role_title-error">{t('LimeHelpdeskForm.errors.required')}</ErrorMessage>
                        )}
                    </div>

                    <div className={styles['Field']}>
                        <div
                            className={classNames(styles['TextareaField'], {
                                [styles['HasValue']]: watch('how_can_we_help'),
                                [styles['HasError']]: errors.how_can_we_help,
                            })}>
                            <TextareaAutosize
                                id="how_can_we_help"
                                minRows={6}
                                className={styles['Textarea']}
                                aria-invalid={!!errors.how_can_we_help}
                                aria-describedby={errors.how_can_we_help ? 'how_can_we_help-error' : null}
                                {...register('how_can_we_help', { required: true })}
                            />
                            <label className={styles['Label']} htmlFor="how_can_we_help">
                                {t('LimeHelpdeskForm.labels.howCanWeHelp')} *
                            </label>
                        </div>
                        {errors.how_can_we_help && (
                            <ErrorMessage id="how_can_we_help-error">
                                {t('LimeHelpdeskForm.errors.required')}
                            </ErrorMessage>
                        )}
                    </div>

                    <div className={styles['Field']}>
                        <div
                            className={classNames(styles['InputField'], {
                                [styles['HasValue']]: watch('email'),
                                [styles['HasError']]: errors.email,
                            })}>
                            <input
                                id="email"
                                type="email"
                                autoComplete="email"
                                maxLength={150}
                                className={styles['Input']}
                                aria-invalid={!!errors.email}
                                aria-describedby={errors.email ? 'email-error' : null}
                                {...register('email', {
                                    required: t('LimeHelpdeskForm.errors.required'),
                                    pattern: {
                                        value: /\S+@\S+\.\S+/,
                                        message: t('LimeHelpdeskForm.errors.validEmail'),
                                    },
                                })}
                            />
                            <label className={styles['Label']} htmlFor="email">
                                {t('LimeHelpdeskForm.labels.email')} *
                            </label>
                        </div>
                        {errors.email && <ErrorMessage id="email-error">{errors.email.message}</ErrorMessage>}
                    </div>
                </div>

                <div className={styles['PrivacyWrapper']}>
                    <input
                        type="checkbox"
                        className={styles['Checkbox']}
                        id="acceptPrivacy"
                        aria-invalid={!!errors.acceptPrivacy}
                        aria-describedby={errors.acceptPrivacy ? 'privacy-error' : null}
                        {...register('acceptPrivacy', { required: true })}
                    />
                    <label
                        dangerouslySetInnerHTML={{
                            __html: formProps?.privacyPolicy?.label,
                        }}
                        className={styles['CheckboxLabel']}
                        htmlFor="acceptPrivacy"
                    />
                </div>
                {errors.acceptPrivacy && (
                    <ErrorMessage id="privacy-error">{t('LimeHelpdeskForm.errors.required')}</ErrorMessage>
                )}

                {!!errors && Object.keys(errors).length > 0 && (
                    <div className={styles['ErrorListWrapper']} role="alert">
                        <ul className={styles['ErrorList']}>
                            {Object.keys(errors).map((key) => (
                                <li key={key} className={styles['ErrorListItem']}>
                                    <Image
                                        width={16}
                                        height={16}
                                        src={`/img/icon-warning.svg`}
                                        alt=""
                                        className={styles['Warning']}
                                    />
                                    <Link href={`#${key}`} className={styles['LinkError']}>
                                        {t(`LimeHelpdeskForm.errors.${key}`, {
                                            defaultValue: t('LimeHelpdeskForm.errors.required'),
                                        })}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className={styles['ButtonWrapper']}>
                    <ButtonPrimary
                        type="submit"
                        icon="icon-send"
                        disabled={submitting}
                        modifiers={['InlineFlex', 'CenterText', 'NarrowIconGap', 'BigButton']}>
                        {t('LimeHelpdeskForm.labels.send')}
                    </ButtonPrimary>
                </div>

                <small className={styles['ReCaptchaDisclaimer']}>
                    {t('ContactForm.recaptcha.protectedBy')}
                    <a href="https://policies.google.com/privacy">{t('ContactForm.recaptcha.privacyPolicy')}</a>
                    {t('ContactForm.recaptcha.and')}
                    <a href="https://policies.google.com/terms">{t('ContactForm.recaptcha.termsOfService')}</a>
                    {t('ContactForm.recaptcha.apply')}
                </small>
            </form>

            <Dialog
                backdropProps={{ className: styles['Backdrop'] }}
                state={dialog}
                className={`${styles['Dialog']} black-glass`}>
                <DialogHeading className={styles['DialogHeader']}>
                    {t('LimeHelpdeskForm.dialog.headline')}
                </DialogHeading>
                <div className={styles['DialogContainer']}>{t('LimeHelpdeskForm.dialog.body')}</div>
                <div className={styles['ButtonContainer']}>
                    <ButtonPrimary modifiers={['TextAlignCenter']} onClick={dialog.close} asLink={true} href="/">
                        {t('LimeHelpdeskForm.dialog.button')}
                    </ButtonPrimary>
                </div>
                <DialogDismiss className={styles['Dismiss']}>
                    <span className="sr-only">{t('LimeHelpdeskForm.dialog.close')}</span>
                </DialogDismiss>
            </Dialog>
        </>
    );
};

const ErrorMessage = ({ children, id }) => (
    <div id={id} className={styles['Error']}>
        <Image width={16} height={16} src={`/img/icon-warning.svg`} alt="" className={styles['Warning']} />
        {children}
    </div>
);

export default LimeHelpdeskForm;
